import { useCallback, useEffect, useState } from "react";
import { fetchCommissionInstallmentsQueue } from "./commissionsApi";

const DEFAULT_PAGINATION = {
  currentPage: 1,
  perPage: 10,
  total: 0,
  lastPage: 1,
};

/**
 * Server-driven installments queue — refetches when page, filters, or search change.
 */
export function useCommissionInstallmentsQueue({
  page,
  perPage,
  dueFilter,
  dueFrom,
  dueTo,
  search,
  queueType = "commission",
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [queueMeta, setQueueMeta] = useState({
    awaitingTotal: 0,
    awaitingCommission: 0,
    awaitingBonus: 0,
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchCommissionInstallmentsQueue({
        page,
        perPage,
        dueFilter,
        dueFrom,
        dueTo,
        search,
        queueType,
      });
      setRows(result.data);
      setPagination(result.pagination);
      setQueueMeta({
        awaitingTotal: result.meta?.awaitingTotal ?? 0,
        awaitingCommission: result.meta?.awaitingCommission ?? 0,
        awaitingBonus: result.meta?.awaitingBonus ?? 0,
      });
    } catch (err) {
      setError(err?.message || "Failed to load installments.");
      setRows([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        lastPage: 1,
      }));
    } finally {
      setLoading(false);
    }
  }, [page, perPage, dueFilter, dueFrom, dueTo, search, queueType]);

  useEffect(() => {
    load();
  }, [load]);

  return {
    loading,
    error,
    rows,
    pagination,
    queueMeta,
    refetch: load,
  };
}
